import { Link } from "react-router-dom";
import { Check, AlertTriangle, ExternalLink, Minus, Plus } from "lucide-react";

export const Section = ({ id, title, kicker, children }) => (
  <section id={id} data-testid={`guide-section-${id}`} className="scroll-mt-28 glass rounded-2xl p-6 sm:p-8">
    {kicker && <div className="mono-label mb-2">{kicker}</div>}
    <h2 className="font-serif text-2xl sm:text-3xl text-white mb-5 border-l-2 border-cyan-300 pl-4">{title}</h2>
    {children}
  </section>
);

export const KV = ({ rows }) => (
  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
    {rows.map(([k, v]) => (
      <div key={k} className="border-b border-white/5 pb-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{k}</div>
        <div className="mt-1 text-slate-200 leading-relaxed">{v || "—"}</div>
      </div>
    ))}
  </div>
);

export const Bullets = ({ items, icon = "check", tone = "cyan" }) => {
  const Icon = icon === "warn" ? AlertTriangle : Check;
  const color = tone === "rose" ? "text-rose-400" : tone === "amber" ? "text-amber-300" : "text-cyan-300";
  return (
    <ul className="space-y-2.5">
      {items.map((t, i) => (
        <li key={i} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
          <Icon size={15} className={`${color} mt-0.5 shrink-0`} /> <span>{t}</span>
        </li>
      ))}
    </ul>
  );
};

export const Steps = ({ items }) => (
  <ol className="space-y-3">
    {items.map((t, i) => (
      <li key={i} className="flex gap-4 text-sm text-slate-300 leading-relaxed">
        <span className="font-mono text-xs text-cyan-300 border border-cyan-300/30 rounded-full w-7 h-7 flex items-center justify-center shrink-0">{i + 1}</span>
        <span className="pt-1">{t}</span>
      </li>
    ))}
  </ol>
);

export const References = ({ items }) => (
  <ol className="space-y-4">
    {items.map(([journal, title, url], i) => (
      <li key={i} className="border border-white/10 rounded-xl p-4 hover:border-cyan-300/30 transition-colors">
        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">[{i + 1}] {journal}</div>
        <div className="mt-1.5 text-sm text-slate-200 leading-relaxed">{title}</div>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" data-testid={`ref-link-${i}`}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-mono text-slate-400 hover:text-cyan-300">
            View Source <ExternalLink size={11} />
          </a>
        )}
      </li>
    ))}
  </ol>
);

export const FAQ = ({ items, open, setOpen }) => (
  <div className="divide-y divide-white/5">
    {items.map(([q, a], i) => (
      <div key={i}>
        <button data-testid={`faq-toggle-${i}`} onClick={() => setOpen(open === i ? null : i)}
          className="w-full flex items-center justify-between gap-4 py-4 text-left text-sm text-white hover:text-cyan-200 transition-colors">
          {q} {open === i ? <Minus size={14} className="shrink-0" /> : <Plus size={14} className="shrink-0" />}
        </button>
        {open === i && <p className="pb-4 text-sm text-slate-300 leading-relaxed">{a}</p>}
      </div>
    ))}
  </div>
);

export const RelatedPeptides = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {items.map((r) => (
      <Link key={r.slug} to={`/peptides/${r.slug}`} data-testid={`related-${r.slug}`}
        className="border border-white/10 rounded-xl p-4 hover:border-cyan-300/40 hover:bg-white/[0.02] transition-colors">
        <div className="font-serif text-lg text-white">{r.name}</div>
        <div className="text-[11px] font-mono text-slate-500 mt-0.5">{r.scientific_name}</div>
      </Link>
    ))}
  </div>
);
